import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Contact as ContactIcon, Plus, Trash2, Store, HeartHandshake, Mail, Phone, Globe, Pencil } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";

const STATUS = [
  { value: "not_contacted", label: "Not contacted", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  { value: "contacted", label: "Contacted", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "responded", label: "Responded", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "partnered", label: "Partnered", cls: "bg-green-100 text-green-800 border-green-200" },
];
const statusCls = (s) => (STATUS.find((x) => x.value === s) || STATUS[0]).cls;
const emptyForm = { name: "", category: "community_program", contact_person: "", email: "", phone: "", address: "", website: "", notes: "", outreach_status: "not_contacted" };

const Contacts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contacts`);
      setItems(res.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const filtered = tab === "all" ? items : items.filter((c) => c.category === tab);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyForm, ...c }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) await axios.put(`${API}/contacts/${editing.id}`, form);
      else await axios.post(`${API}/contacts`, form);
      toast.success(editing ? "Contact updated" : "Contact added");
      setOpen(false); fetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (c, outreach_status) => {
    try { await axios.put(`${API}/contacts/${c.id}`, { outreach_status }); fetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try { await axios.delete(`${API}/contacts/${id}`); toast.success("Contact deleted"); fetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>Community Contacts</h1>
          <p className="text-slate-500 mt-1">Track outreach to El Paso County programs and retail partners</p>
        </div>
        <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white gap-2" data-testid="add-contact-btn">
          <Plus className="w-4 h-4" /> Add Contact
        </Button>
      </div>

      <div className="flex gap-2">
        {[["all", "All"], ["community_program", "Community Programs"], ["retail", "Retail Partners"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${tab === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {l}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><ContactIcon className="w-5 h-5 text-amber-600" />Directory</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-slate-500 py-8 text-center">Loading...</p> : filtered.length === 0 ? (
            <p className="text-slate-500 py-8 text-center" data-testid="no-contacts">No contacts.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Outreach Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium text-slate-900">
                        {c.category === "retail" ? <Store className="w-4 h-4 text-slate-400" /> : <HeartHandshake className="w-4 h-4 text-amber-500" />}
                        {c.name}
                      </div>
                      {c.address && <div className="text-xs text-slate-400 mt-0.5 ml-6">{c.address}</div>}
                      {c.notes && <div className="text-xs text-slate-500 mt-0.5 ml-6 italic">{c.notes}</div>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {c.contact_person && <div>{c.contact_person}</div>}
                      {c.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div>}
                      {c.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>}
                      {c.website && <div className="flex items-center gap-1"><Globe className="w-3 h-3" />{c.website}</div>}
                    </TableCell>
                    <TableCell>
                      <Select value={c.outreach_status} onValueChange={(v) => changeStatus(c, v)}>
                        <SelectTrigger className="w-40 h-8" data-testid={`status-select-${c.id}`}>
                          <Badge variant="outline" className={statusCls(c.outreach_status)}>{(STATUS.find((s) => s.value === c.outreach_status) || STATUS[0]).label}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(c.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-contact-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>{editing ? "Edit Contact" : "Add Contact"}</DialogTitle>
            <DialogDescription>Track an organization and your outreach progress.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Organization Name *</Label><Input className="mt-1" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="contact-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="community_program">Community Program</SelectItem>
                  <SelectItem value="retail">Retail Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Contact Person</Label><Input className="mt-1" data-testid="contact-person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
              <div><Label>Phone</Label><Input className="mt-1" data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><Label>Email</Label><Input className="mt-1" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Address</Label><Input className="mt-1" data-testid="contact-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Website</Label><Input className="mt-1" data-testid="contact-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
            <div>
              <Label>Outreach Status</Label>
              <Select value={form.outreach_status} onValueChange={(v) => setForm({ ...form, outreach_status: v })}>
                <SelectTrigger className="mt-1" data-testid="contact-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea className="mt-1" rows={3} data-testid="contact-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <Button onClick={save} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-contact-btn">
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Contact"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
